/*
 * MOCK ONLY — visual concept for the Surgical Suite document redesign.
 * Pure presentational, hardcoded sample data, no backend calls.
 * Route: /mock/surgical-suite
 */
import React, { useState } from 'react';
import { Mic, MicOff, Printer, FileDown, Share2, Lock, Save, QrCode, ShieldCheck } from 'lucide-react';

const NAVY = '#07122f';
const TEAL = '#007f73';
const PAPER = '#fdfcf7';

const serif = "'Playfair Display', 'Merriweather', Georgia, serif";
const mono = "'JetBrains Mono', 'IBM Plex Mono', monospace";
const sans = "'Manrope', 'Work Sans', system-ui, sans-serif";

export default function SurgicalSuiteMock() {
  const [dictating, setDictating] = useState(true);
  const activeField = 'operative_findings'; // simulated active section

  return (
    <div className="min-h-screen" style={{ background: '#e8eaef', fontFamily: sans }}>
      {/* ── Top toolbar (app chrome, not part of document) ── */}
      <div className="sticky top-0 z-40 border-b border-slate-200" style={{ background: NAVY }}>
        <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-bold tracking-[.18em] uppercase" style={{ color: TEAL, filter: 'brightness(1.6)' }}>Surgical Suite</span>
            <span className="text-white text-sm font-semibold">Post-Op Discharge Summary</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setDictating(!dictating)}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[12px] font-bold text-white transition"
              style={{ background: dictating ? '#ef4444' : TEAL }}
              data-testid="mock-master-dictate"
            >
              {dictating ? <><MicOff size={13} /> Stop dictation</> : <><Mic size={13} /> Dictate summary</>}
            </button>
            {[['Save draft', Save], ['Seal & lock', Lock], ['Share', Share2], ['PDF & print', FileDown], ['Print', Printer]].map(([label, Icon]) => (
              <button key={label} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/85 border border-white/25 hover:bg-white/10">
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Live interim dictation banner ── */}
      {dictating && (
        <div className="sticky top-[46px] z-30" style={{ background: '#fef3c7', borderBottom: '1px solid #f59e0b' }}>
          <div className="max-w-5xl mx-auto px-4 py-2 flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-[11px] font-bold text-red-600">
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /> LISTENING
            </span>
            <span className="text-[12px] text-amber-900 italic">
              "…operative findings <span style={{ color: TEAL, fontWeight: 700 }}>(field switched)</span> — gallbladder distended, single large calculus at the neck, no perforation…"
            </span>
            <span className="ml-auto text-[10px] text-amber-700 font-semibold uppercase tracking-wider">Active field: Operative Findings</span>
          </div>
        </div>
      )}

      {/* ── THE DOCUMENT SHEET ── */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div
          className="relative rounded-sm"
          style={{
            background: PAPER,
            border: `2px solid ${NAVY}`,
            boxShadow: '0 25px 60px -12px rgba(7,18,47,.25), 0 0 0 6px #fdfcf7, 0 0 0 7px #c7cdd9',
            padding: '44px 52px',
          }}
        >
          {/* CR No — top-right inactive placeholder */}
          <div className="absolute top-5 right-6 text-right">
            <div className="inline-block px-2.5 py-1 rounded-sm" style={{ border: `1px dashed #94a3b8`, background: '#f1f5f9' }}>
              <span style={{ fontFamily: mono, fontSize: 11, color: '#64748b', letterSpacing: '.06em' }}>CR NO · RESERVED · HMS SYNC</span>
            </div>
          </div>

          {/* ── LEVEL 2: Hospital letterhead ── */}
          <div className="text-center pb-4" style={{ borderBottom: `3px double ${NAVY}` }}>
            <div style={{ fontFamily: serif, fontSize: 26, fontWeight: 700, color: NAVY, letterSpacing: '.02em' }}>
              City Care Multispeciality Hospital
            </div>
            <div style={{ fontSize: 12, color: '#475569', marginTop: 3 }}>
              Department of General & Laparoscopic Surgery · 14, Medical Enclave, Sector 21, Gurugram, Haryana 122016
            </div>
            <div style={{ fontFamily: mono, fontSize: 10.5, color: '#64748b', marginTop: 2 }}>
              Document created: 23 Sep 2026 · 14:32 IST
            </div>
          </div>

          {/* ── LEVEL 1: Admitted Under — the anchor ── */}
          <div className="flex items-end justify-between pt-4 pb-3" style={{ borderBottom: `1.5px solid ${NAVY}` }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.14em', color: TEAL, textTransform: 'uppercase' }}>Admitted Under</div>
              <div style={{ fontFamily: serif, fontSize: 21, fontWeight: 700, color: NAVY }}>Dr. Vivek Sharma, MS (Gen. Surgery)</div>
              <div style={{ fontSize: 11.5, color: '#475569' }}>Principal Surgeon · Regn. No. HMC-28417</div>
            </div>
            <div className="text-right">
              <div style={{ fontFamily: serif, fontSize: 15, fontWeight: 700, color: NAVY, letterSpacing: '.06em' }}>DISCHARGE SUMMARY</div>
              <div style={{ fontSize: 10.5, color: '#64748b' }}>Post-Operative · IPD</div>
            </div>
          </div>

          {/* ── LEVEL 3: Patient identity table ── */}
          <table className="w-full mt-4" style={{ borderCollapse: 'collapse', fontSize: 12.5 }}>
            <tbody>
              <tr>
                <Td label="Patient Name" value="Ramesh Chandra Gupta" w="34%" />
                <Td label="Age / Sex" value="58 yrs · Male" w="22%" />
                <Td label="Mobile" value="+91 98110 43210" w="22%" mono />
                <Td label="Date of Discharge" value="23 Sep 2026" w="22%" mono />
              </tr>
              <tr>
                <td colSpan={4} style={{ border: '1px solid #94a3b8', padding: '6px 10px' }}>
                  <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.1em', color: '#64748b', textTransform: 'uppercase' }}>Address&nbsp;&nbsp;</span>
                  <span style={{ color: NAVY }}>H.No. 412, Pocket B, Ashok Vihar Phase II, Delhi 110052</span>
                </td>
              </tr>
            </tbody>
          </table>

          {/* ── Clinical sections (ruled) ── */}
          <DocSection title="Diagnosis">
            Acute calculous cholecystitis
          </DocSection>

          <DocSection title="Operation Performed">
            Laparoscopic Cholecystectomy <span style={{ color: '#64748b', fontSize: 11 }}>(from department surgery catalog)</span>
          </DocSection>

          <DocSection title="Operative Findings" active={dictating && activeField === 'operative_findings'}>
            <ul className="list-disc pl-5 space-y-1" style={{ margin: 0 }}>
              <li>Gallbladder distended, thick-walled, densely adherent to omentum.</li>
              <li>Single large calculus (approx. 2.4 cm) impacted at the neck.</li>
              <li>Calot's triangle dissected carefully; cystic artery and duct clipped ×2 and divided.</li>
              <li>No bile leak; no perforation; no stones spilled. Haemostasis confirmed.</li>
            </ul>
            {dictating && activeField === 'operative_findings' && (
              <div className="mt-1.5 flex items-center gap-1.5" style={{ color: TEAL, fontSize: 11, fontWeight: 600 }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: TEAL }} />
                interim: "…liver bed oozing minimal, washed with saline…"
              </div>
            )}
          </DocSection>

          <DocSection title="Post-Operative Plan">
            Keep NPO for 6 hours, then sips of clear liquids. Monitor vitals 4-hourly. IV antibiotics to continue for 48 hours. Ambulate by evening. Drain output to be reviewed morning rounds.
          </DocSection>

          <DocSection title="Treatments Advised on Discharge">
            <ul className="list-disc pl-5 space-y-1" style={{ margin: 0 }}>
              <li>Tab. Augmentin 625 mg — 1 BD × 5 days</li>
              <li>Cap. Pantoprazole 40 mg — 1 OD before breakfast × 10 days</li>
              <li>Tab. Ultracet — 1 SOS for pain (max 3/day)</li>
            </ul>
          </DocSection>

          <DocSection title="Follow-Up Schedule" last>
            <table className="w-full" style={{ fontSize: 12.5, borderCollapse: 'collapse' }}>
              <tbody>
                <tr><td style={{ fontFamily: mono, width: 110, color: TEAL, fontWeight: 700 }}>30 Sep 2026</td><td style={{ color: NAVY }}>Suture / staple removal + wound inspection — OPD Room 4</td></tr>
                <tr><td style={{ fontFamily: mono, width: 110, color: TEAL, fontWeight: 700 }}>07 Oct 2026</td><td style={{ color: NAVY }}>Histopathology report review with Principal Surgeon</td></tr>
              </tbody>
            </table>
          </DocSection>

          {/* ── Signature + QR verification block ── */}
          <div className="flex items-end justify-between mt-10 pt-4" style={{ borderTop: `1.5px solid ${NAVY}` }}>
            <div>
              <div style={{ height: 34, fontFamily: serif, fontStyle: 'italic', fontSize: 17, color: NAVY }}>A. Singh</div>
              <div style={{ borderTop: '1px solid #334155', paddingTop: 3, fontSize: 11, color: '#334155' }}>
                <b>Dr. Ankit Singh, MBBS</b> — Senior Resident (signatory)
                <div style={{ fontSize: 10, color: '#64748b' }}>Prepared under supervision of Dr. Vivek Sharma</div>
              </div>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center rounded-sm" style={{ width: 64, height: 64, border: `1.5px solid ${NAVY}`, background: '#fff' }}>
                <QrCode size={44} style={{ color: NAVY }} />
              </div>
              <div style={{ fontSize: 9, color: '#64748b', marginTop: 3, letterSpacing: '.08em', textTransform: 'uppercase' }}>Scan to verify</div>
            </div>
            <div className="text-right">
              <div style={{ height: 34 }} />
              <div style={{ borderTop: '1px solid #334155', paddingTop: 3, fontSize: 11, color: '#334155' }}>
                <b>Dr. Vivek Sharma, MS</b> — Principal Surgeon
                <div style={{ fontSize: 10, color: '#64748b' }}>Medical Seal · HMC-28417</div>
              </div>
            </div>
          </div>

          {/* ── Hospital branding + DocSite footer ── */}
          <div className="flex items-center justify-between mt-6 pt-3" style={{ borderTop: '1px dashed #94a3b8' }}>
            <div style={{ fontSize: 10, color: '#64748b' }}>
              City Care Multispeciality Hospital · 14, Medical Enclave, Sector 21, Gurugram · +91 124 400 8800 · citycare.example.in
            </div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: NAVY }}>
              <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center" style={{ background: TEAL }}>
                <span style={{ color: '#fff', fontSize: 8, fontWeight: 900 }}>D</span>
              </div>
              <span style={{ color: '#fff', fontSize: 9.5, fontWeight: 700, letterSpacing: '.06em' }}>Built on the DocSite platform</span>
            </div>
          </div>

          {/* Sealed ribbon (visual state demo) */}
          <div className="absolute top-16 -right-2 px-3 py-1 text-white text-[10px] font-bold tracking-[.15em] rotate-45" style={{ background: TEAL, boxShadow: '0 2px 6px rgba(0,0,0,.2)' }}>
            SEALED
          </div>
        </div>

        {/* Hint strip under the document */}
        <div className="mt-5 flex items-center justify-center gap-2 text-[11px]" style={{ color: '#64748b' }}>
          <ShieldCheck size={13} style={{ color: TEAL }} />
          Voice-gated · Encrypted at rest · Offline-first draft · QR-verifiable — this sheet is the printable source of truth
        </div>
      </div>
    </div>
  );
}

function Td({ label, value, mono: isMono, w }) {
  return (
    <td style={{ border: '1px solid #94a3b8', padding: '6px 10px', width: w }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.1em', color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontFamily: isMono ? "'JetBrains Mono', monospace" : sans, fontWeight: 600, color: '#07122f' }}>{value}</div>
    </td>
  );
}

function DocSection({ title, children, active, last }) {
  return (
    <div className="mt-5" style={!last ? { borderBottom: '1px solid #cbd5e1', paddingBottom: 14 } : { paddingBottom: 6 }}>
      <div className="flex items-center gap-2">
        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: '#07122f', borderBottom: `2px solid ${active ? '#ef4444' : '#07122f'}`, paddingBottom: 2, marginBottom: 8 }}>
          {title}
        </div>
        {active && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold text-white mb-2" style={{ background: '#ef4444' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> RECORDING HERE
          </span>
        )}
      </div>
      <div style={{ fontSize: 13.5, lineHeight: 1.65, color: '#1e293b' }}>{children}</div>
    </div>
  );
}
